import { AnimatedContainer, AnimatedSection } from '@components/animated'
import CollapsibleAlert from '@components/collapsibleAlert/CollapsibleAlert'
import { FloatingSave } from '@components/form/FloatingSave'
import { Section } from '@components/index'
import { SectionTitle } from '@components/section/SectionTitle'
import {
  CivicFacilitySelect,
  EquipmentSelect,
  FloorSelect,
  LocationTreeSelect,
  RangeInput,
  RoomSelect,
  ServicesSelect
} from '@features/applications/components'
import {
  type ApartmentPrefill,
  matchLocationId,
  matchRoomId,
  prefillRentAreaRanges
} from '@features/applications/utils/apartmentPrefill'
import { flattenLocations } from '@features/applications/utils/locationOptions'
import { useLocations } from '@services/common/useLocation'
import { useRooms } from '@services/common'
import { House, MapPin, Sofa, Users } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { ApplicationDetail, ApplicationWrite } from 'types/api'

import { Checkbox } from '@/components/ui/checkbox'
import { Combobox } from '@/components/ui/combobox'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'

import { RoommateManager } from './RoommateManager'

const TODAY_ISO = new Date().toISOString().slice(0, 10)
const SHOW_SMOKING = false

const MAXIMAL_RENTAL_PERIOD = [
  { label: 'form.common.years.one', value: 1 },
  { label: 'form.common.years.two', value: 2 },
  { label: 'form.common.years.three', value: 3 },
  { label: 'form.common.years.more', value: 4 }
]

function numOrNull(v: unknown): number | null {
  return v == null || v === '' ? null : Number(v)
}

function toIds(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v
    .map((x) => (typeof x === 'string' ? x : ((x as { id?: string })?.id ?? '')))
    .filter(Boolean)
}

export interface ApplicationProps {
  data?: ApplicationDetail
  prefill?: ApartmentPrefill
  handleSubmit: (body: ApplicationWrite) => void
  isDisabled?: boolean
  isFullWidth?: boolean
  isAdminView?: boolean
}

export function ApplicationForm(props: ApplicationProps) {
  const { data, prefill, isFullWidth = true, isDisabled, isAdminView } = props
  const { t } = useTranslation('application')
  const rooms = useRooms()
  const { data: locationsData } = useLocations()
  const prefillAppliedRef = useRef(false)
  const [showFloatingSave, setShowFloatingSave] = useState(false)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    location: true,
    flat: true,
    equipment: false,
    roommates: false
  })

  const prefillRanges = prefill ? prefillRentAreaRanges(prefill) : null

  const defaultValues = {
    locations: toIds(data?.locations),
    maximal_rental_period: data?.maximal_rental_period ?? undefined,
    arrival_date: data?.arrival_date ?? TODAY_ISO,
    accept_later_arrival: data?.accept_later_arrival ?? false,
    rooms: toIds(data?.rooms),
    floor: toIds(data?.floor),
    // Existing applications keep their stored ranges (including "no
    // preference" = null); the defaults only seed a brand-new application.
    rent_min: prefillRanges?.rent_min ?? (data ? (data.rent_min ?? null) : 300),
    rent_max: prefillRanges?.rent_max ?? (data ? (data.rent_max ?? null) : 500),
    area_min: prefillRanges?.area_min ?? (data ? (data.area_min ?? null) : 30),
    area_max: prefillRanges?.area_max ?? (data ? (data.area_max ?? null) : 50),
    equipment: toIds(data?.equipment),
    services: toIds(data?.services),
    civic_facilities: toIds(data?.civic_facilities),
    pet_friendly: data?.pet_friendly ?? false,
    unfurnished_allowed: data?.unfurnished_allowed ?? false,
    roommates_number_estimation: data?.roommates_number_estimation ?? undefined,
    underaged_roommates: data?.underaged_roommates ?? undefined
  } as unknown as ApplicationWrite

  const { control, handleSubmit, watch, reset, setValue } =
    useForm<ApplicationWrite>({ defaultValues })

  const locationOptions = useMemo(
    () => flattenLocations((locationsData as Parameters<typeof flattenLocations>[0]) ?? []),
    [locationsData]
  )

  useEffect(() => {
    if (prefill) {
      setShowFloatingSave(true)
    }
  }, [prefill])

  useEffect(() => {
    if (!prefill || prefillAppliedRef.current) return
    if (rooms.data === undefined || locationsData === undefined) return

    const roomId = matchRoomId(rooms.data ?? [], prefill.room_count)
    if (roomId) {
      setValue('rooms', [roomId] as never, { shouldDirty: true })
    }

    const locationId = matchLocationId(locationOptions, prefill.location)
    if (locationId) {
      setValue('locations', [locationId] as never, { shouldDirty: true })
    }

    prefillAppliedRef.current = true
  }, [prefill, rooms.data, locationsData, locationOptions, setValue])

  useEffect(() => {
    const sub = watch((_, { type }) => {
      if (type === 'change') setShowFloatingSave(true)
    })
    return () => sub.unsubscribe()
  }, [watch])

  const sectionOpenProps = (key: string) => ({
    open: openSections[key],
    onOpenChange: (o: boolean) => setOpenSections((s) => ({ ...s, [key]: o }))
  })

  const discardChanges = () => {
    reset(defaultValues)
    setShowFloatingSave(false)
  }

  const onValid = (values: ApplicationWrite) => {
    props.handleSubmit(values)
    setShowFloatingSave(false)
  }

  const YesNo = ({ name, label }: { name: keyof ApplicationWrite; label: string }) => (
    <Controller
      control={control}
      name={name as never}
      render={({ field }) => (
        <label className="flex items-center justify-between gap-3 rounded-2xl border border-ink/[0.08] bg-ink/[0.015] px-4 py-3">
          <span className="text-sm font-medium leading-snug text-ink">
            {label}
          </span>
          <Switch
            checked={!!field.value}
            onCheckedChange={field.onChange}
            disabled={isDisabled}
          />
        </label>
      )}
    />
  )

  return (
    <form onSubmit={handleSubmit(onValid)} className="mb-10 mt-6">
      <AnimatedContainer>
        <div className="space-y-4">
          {/* Location */}
          <AnimatedSection>
            <Section
              {...sectionOpenProps('location')}
              title={
                <SectionTitle
                  title={t('form.sections.location.title')}
                  icon={<MapPin className="h-4 w-4" />}
                />
              }
            >
              <div className="grid grid-cols-1 gap-4 p-5 pt-2 lg:grid-cols-2">
                <Controller
                  control={control}
                  name={'locations' as never}
                  render={({ field }) => (
                    <LocationTreeSelect
                      value={(field.value as string[]) ?? []}
                      onChange={field.onChange}
                      disabled={isDisabled}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={'maximal_rental_period' as never}
                  render={({ field }) => (
                    <div>
                      <Label className="mb-1.5 block">
                        {t('form.sections.location.rentalDuration')}
                      </Label>
                      <Combobox
                        searchable={false}
                        disabled={isDisabled}
                        value={
                          field.value != null ? String(field.value) : undefined
                        }
                        onChange={(v) => field.onChange(v ? Number(v) : undefined)}
                        placeholder={t('form.common.selectOption')}
                        options={MAXIMAL_RENTAL_PERIOD.map((p) => ({
                          value: String(p.value),
                          label: t(p.label)
                        }))}
                      />
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name={'arrival_date' as never}
                  render={({ field }) => (
                    <div>
                      <Label className="mb-1.5 block">
                        {t('form.sections.location.arrival')}
                      </Label>
                      <DatePicker
                        clearable
                        min={TODAY_ISO}
                        disabled={isDisabled}
                        value={(field.value as string) ?? undefined}
                        onChange={field.onChange}
                      />
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name={'accept_later_arrival' as never}
                  render={({ field }) => (
                    <label className="flex items-center gap-2.5 self-end pb-2.5">
                      <Checkbox
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                        disabled={isDisabled}
                      />
                      <span className="text-sm font-medium text-ink">
                        {t('form.sections.location.allowLaterArrival')}
                      </span>
                    </label>
                  )}
                />
              </div>
            </Section>
          </AnimatedSection>

          {/* Housing */}
          <AnimatedSection>
            <Section
              {...sectionOpenProps('flat')}
              title={
                <SectionTitle
                  title={t('form.sections.housing.title')}
                  icon={<House className="h-4 w-4" />}
                />
              }
            >
              <div className="grid grid-cols-1 gap-4 p-5 pt-2 lg:grid-cols-2">
                <Controller
                  control={control}
                  name={'rooms' as never}
                  render={({ field }) => (
                    <RoomSelect
                      value={(field.value as string[]) ?? []}
                      onChange={field.onChange}
                      disabled={isDisabled}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={'floor' as never}
                  render={({ field }) => (
                    <FloorSelect
                      value={(field.value as string[]) ?? []}
                      onChange={field.onChange}
                      disabled={isDisabled}
                    />
                  )}
                />

                <RangeInput
                  label="form.sections.housing.rentRange"
                  labelMin="form.sections.housing.minRent"
                  labelMax="form.sections.housing.maxRent"
                  min={100}
                  max={2000}
                  step={10}
                  unit="€"
                  disabled={isDisabled}
                  value={[numOrNull(watch('rent_min')), numOrNull(watch('rent_max'))]}
                  onChange={([lo, hi]) => {
                    setValue('rent_min', lo as never, { shouldDirty: true })
                    setValue('rent_max', hi as never, { shouldDirty: true })
                    setShowFloatingSave(true)
                  }}
                />
                <RangeInput
                  label="form.sections.housing.area"
                  labelMin="form.sections.housing.minArea"
                  labelMax="form.sections.housing.maxArea"
                  min={15}
                  max={130}
                  step={1}
                  unit="m²"
                  disabled={isDisabled}
                  value={[numOrNull(watch('area_min')), numOrNull(watch('area_max'))]}
                  onChange={([lo, hi]) => {
                    setValue('area_min', lo as never, { shouldDirty: true })
                    setValue('area_max', hi as never, { shouldDirty: true })
                    setShowFloatingSave(true)
                  }}
                />
              </div>
            </Section>
          </AnimatedSection>

          {/* Equipment */}
          <AnimatedSection>
            <Section
              {...sectionOpenProps('equipment')}
              title={
                <SectionTitle
                  title={t('form.sections.equipment.title')}
                  icon={<Sofa className="h-4 w-4" />}
                />
              }
            >
              <div className="space-y-5 p-5 pt-2">
                <div
                  className={
                    isFullWidth
                      ? 'grid grid-cols-1 gap-4 lg:grid-cols-3'
                      : 'grid grid-cols-1 gap-4 lg:grid-cols-2'
                  }
                >
                  <Controller
                    control={control}
                    name={'equipment' as never}
                    render={({ field }) => (
                      <EquipmentSelect
                        value={(field.value as string[]) ?? []}
                        onChange={field.onChange}
                        disabled={isDisabled}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={'services' as never}
                    render={({ field }) => (
                      <ServicesSelect
                        value={(field.value as string[]) ?? []}
                        onChange={field.onChange}
                        disabled={isDisabled}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={'civic_facilities' as never}
                    render={({ field }) => (
                      <CivicFacilitySelect
                        value={(field.value as string[]) ?? []}
                        onChange={field.onChange}
                        disabled={isDisabled}
                      />
                    )}
                  />
                </div>
                <div className="border-t border-ink/[0.06] pt-5">
                  <p className="m-0 mb-3 text-sm font-semibold text-ink">
                    {t('form.sections.equipment.preferences')}
                  </p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {SHOW_SMOKING && (
                      <YesNo
                        name="smoking_allowed"
                        label={t('form.sections.equipment.smokingAllowed')}
                      />
                    )}
                    <YesNo
                      name="pet_friendly"
                      label={t('form.sections.equipment.petFriendly')}
                    />
                    <YesNo
                      name="unfurnished_allowed"
                      label={t('form.sections.equipment.unequipped')}
                    />
                  </div>
                </div>
              </div>
            </Section>
          </AnimatedSection>

          {/* Roommates */}
          <AnimatedSection>
            <Section
              {...sectionOpenProps('roommates')}
              title={
                <SectionTitle
                  title={t('form.sections.roommates.title')}
                  icon={<Users className="h-4 w-4" />}
                />
              }
            >
              <div className="space-y-5 p-5 pt-2">
                <div>
                  <div className="mb-1 text-sm font-semibold text-ink">
                    {t('form.sections.roommates.roommates_title')}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t('form.sections.roommates.roommates_description')}
                  </div>
                </div>

                <CollapsibleAlert
                  description={t('form.sections.roommates.roommates_info_alert')}
                  label={t('form.sections.roommates.roommates_info_alert_label')}
                  type="info"
                  showIcon
                  isExpanded
                />

                <RoommateManager
                  disabled={isDisabled || false}
                  roommates={data?.roommates}
                  applicationId={data?.id}
                  linkToProfile={isAdminView}
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Controller
                    control={control}
                    name={'roommates_number_estimation' as never}
                    render={({ field }) => (
                      <div>
                        <Label className="mb-1.5 block">
                          {t('form.labels.numberOfRoommates')}
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          max={10}
                          disabled={isDisabled}
                          placeholder={t('form.placeholders.numberOfRoommates')}
                          value={(field.value as number | undefined) ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ''
                                ? undefined
                                : Number(e.target.value)
                            )
                          }
                        />
                      </div>
                    )}
                  />
                  <Controller
                    control={control}
                    name={'underaged_roommates' as never}
                    render={({ field }) => (
                      <div>
                        <Label className="mb-1.5 block">
                          {t('form.sections.roommates.underagedChildren')}
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          max={5}
                          disabled={isDisabled}
                          placeholder={t('form.placeholders.underaged_roommates')}
                          value={(field.value as number | undefined) ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ''
                                ? undefined
                                : Number(e.target.value)
                            )
                          }
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
            </Section>
          </AnimatedSection>
        </div>
      </AnimatedContainer>
      {showFloatingSave && !isDisabled && (
        <FloatingSave discard={discardChanges} />
      )}
    </form>
  )
}
